/**
 * Utility functions for profile completion status checking
 */

import { FetchUserAttributesOutput } from 'aws-amplify/auth';

/**
 * Checks if a user has all required profile fields populated
 * Used for backward compatibility with existing users who registered before
 * the profile completion feature was added
 * 
 * @param attributes - User attributes from Cognito
 * @returns true if all required profile fields are present and non-empty
 */
export function hasAllProfileFields(attributes: FetchUserAttributesOutput): boolean {
  const requiredFields = [
    'name',           // First name
    'family_name',    // Last name
    'phone_number',   // Phone number with country code
    'custom:username' // Username
  ];

  return requiredFields.every(field => {
    const value = attributes[field];
    return value !== undefined && value !== null && value.trim() !== '';
  });
}

/**
 * Determines if a user's profile is complete based on the custom:profileComplete
 * attribute and backward compatibility rules
 * 
 * @param attributes - User attributes from Cognito
 * @returns true if profile is complete, false otherwise
 */
export function isProfileComplete(attributes: FetchUserAttributesOutput): boolean {
  const profileStatus = attributes['custom:profileComplete'];
  
  // If profileComplete attribute exists, use it
  if (profileStatus !== undefined && profileStatus !== null) {
    return profileStatus === 'complete';
  }
  
  // Backward compatibility: If attribute is missing, check if all profile fields exist
  // Existing users with all fields should be treated as complete
  return hasAllProfileFields(attributes);
}
